import { Grid, Typography } from "@material-ui/core";
import DropdownFilter from "components/DropdownFilter";
import EmptyPlaceholder from "components/EmptyPlaceholder/EmptyPlaceholder";
import LotsDropdown from "components/SharedDropdowns/LotsDropDown";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { selectTenantName } from "features/user/userSlice";
import { useGetVehiclesQuery } from "features/vehicles/vehicleSlice";
import { useGetStatusesForClientQuery } from "features/vehicles/VehicleStatus/vehicleStatusSlice";
import { IconCar, IconLot, IconPmfLogo } from "icons";
import { groupBy, sortBy } from "lodash";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import FilterService from "services/filterService";
import { theme } from "styles/theme";
import { arrayToObject } from "utils/object-util";
import WidgetTitle from "../WidgetTitle/WidgetTitle";
import { assetLocationWidgetStyle } from "./style";

const AssetLocationWidget = () => {
    const classes = assetLocationWidgetStyle();

    const tenantName = useSelector(selectTenantName);

    let { data: vehiclesData, error: vehicleError, isLoading: isLoadingVehicles, refetch: refetchVehicles } = useGetVehiclesQuery({ includeHistory: false });
    let { data: lots, error: lotError, isLoading: isLoadingLots, refetch: refetchLots } = useGetLotsQuery();
    let { data: clientVehicleStatuses, error: clientVehicleStatusesError, loading: clientVehicleStatusesLoading } = useGetStatusesForClientQuery({ clientId: 0 })

    vehiclesData = vehiclesData || [];
    lots = lots || [];
    clientVehicleStatuses = clientVehicleStatuses?.filter(s => s.onLot) || [];

    const { dashboardFilters } = useSelector(state => state.fleetManagementDashboardState);

    const loading = isLoadingVehicles || isLoadingLots || clientVehicleStatusesLoading;

    const [tenantLotFilter, setTenantLotFilter] = useState(1);

    const [filters, setFilters] = useState({
        'lotId': { value: [] },
        'vehicleStatusId': { value: [] }
    })

    const onDropFilter = (filterValue, prop) => {
        setFilters({ ...filters, [prop]: { value: filterValue } });
    };

    const filteredData = FilterService.filter(filters, null, vehiclesData, [])?.filter(f => (dashboardFilters.isDefaultAccount ? (!dashboardFilters.clientId || dashboardFilters.clientId == f?.clientId)
        : (!dashboardFilters.clientAccountId || dashboardFilters.clientAccountId == f?.clientAccountId)));;

    const groupedByLots = groupBy(filteredData, 'lotId');
    const lotsLookup = arrayToObject(lots);

    const finalData = sortBy(Object.keys(groupedByLots).map(key => ({
        lotId: +key,
        lotName: lotsLookup?.[key]?.name,
        count: groupedByLots[key]?.length,
        ownedByTenant: lotsLookup?.[key]?.ownedByTenant ? 1 : 2
    }
    ))?.filter(item => tenantLotFilter ? item?.ownedByTenant == tenantLotFilter : true), [d => d.lotName]);

    const CustomTooltip = ({ payload }) => {
        return (
            <div className={classes.graphTootltip}>
                <p>
                    <div style={{ fontSize: 11 }}>Lot:</div>
                    <div style={{ fontSize: 10 }}><b>{`${lotsLookup?.[payload?.[0]?.payload.lotId]?.name} ${lotsLookup?.[payload?.[0]?.payload.lotId]?.addressLine1}`}</b></div>
                    <div style={{ color: theme.palette.primary.main }}><b>{payload?.[0]?.payload?.count} assets</b></div>
                </p>
            </div>
        );
    };

    return <Grid container direction="column">
        <WidgetTitle
            title='Assets Locations'
            info={`This shows how many of your Assets are on each of the Lots now. 
                    You can filter by Lot and Status to see what is the disposition of your fleet at each location.`}
            loading={loading}
            filtersList={{ ...filters, 'tenantLotFilter': { value: tenantLotFilter } }}
            filters={
                <Grid container spacing={1}>
                    <Grid className={classes.filterIcon} item xs={2}>
                        <IconPmfLogo height={17} fill="#0090fe" />
                    </Grid>
                    <Grid item xs={10}>
                        <DropdownFilter
                            identifier="ownedByTenant"
                            value={tenantLotFilter}
                            onSelect={(val) => setTenantLotFilter(val)}
                            values={[
                                { id: 1, name: `${tenantName} Lots` },
                                { id: 2, name: `Non ${tenantName} Lots` },
                            ]}
                            showEmpty={true}
                        />
                    </Grid>
                    <Grid className={classes.filterIcon} item xs={2}>
                        <IconLot />
                    </Grid>
                    <Grid item xs={10}>
                        <LotsDropdown
                            title='Lot'
                            identifier='lot'
                            values={lots}
                            onSelect={(val) => onDropFilter(val, 'lotId')}
                            value={filters?.['lotId']?.value}
                            showEmpty={true}
                            multiple={true}
                            ownedByTenant={tenantLotFilter == 1}
                        />
                    </Grid>
                    <Grid className={classes.filterIcon} item xs={2}>
                        <IconCar width={20} />
                    </Grid>
                    <Grid item xs={10}>
                        <DropdownFilter
                            title="Status"
                            identifier="status"
                            value={filters?.['vehicleStatusId']?.value}
                            onSelect={(val) => onDropFilter(val, 'vehicleStatusId')}
                            values={clientVehicleStatuses ?? []}
                            showEmpty={true}
                            multiple={true}
                        />
                    </Grid>
                </Grid>
            } />
        <Grid item style={{ height: 400, paddingRight: '0.5rem' }}>
            {!finalData?.length ? (
                <EmptyPlaceholder text='No Assets' />
            ) : finalData.length === 1 ? (
                <div className={classes.singleLotCard}>
                    <Typography className={classes.singleLotCount}>{finalData[0].count}</Typography>
                    <Typography className={classes.singleLotLabel}>Assets at {finalData[0].lotName}</Typography>
                </div>
            ) : (
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={finalData}
                        margin={{ top: 30, right: 50, left: 10, bottom: 5 }}
                    >
                        <XAxis
                            type="category"
                            axisLine={false}
                            interval={~~(finalData?.length / 40)}
                            dataKey="lotName"
                            tickLine={false}
                            tick={{
                                color: theme.palette.primary.main,
                                fontSize: '0.8em',
                                fontWeight: '500',
                                dx: -4,
                            }}
                            angle={-90}
                            textAnchor="end"
                            height={50}
                        />
                        <YAxis
                            type="number"
                            dataKey="count"
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(value) => Math.round(value) == value ? value : ''}
                        />
                        <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ fill: '#F3F5F6' }}
                        />
                        <Bar
                            dataKey="count"
                            fill={theme.palette.primary.main}
                            maxBarSize={5}
                        >
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}
        </Grid>
    </Grid>
}
export default AssetLocationWidget;